import { graphql, useStaticQuery } from 'gatsby';
import { PerformerType } from '../components/hoc/constants/performer.constants';
import useProduct from './useProduct';
import { TestOption, getABFlow } from '../utils/abFlow';

interface TranslationService {
	t: (key: string) => string;
}

type TranslationNode = {
	name: string;
	childPlainText: {
		content: string;
	};
};

type TranslationQueryResponse = {
	default: {
		nodes: TranslationNode[];
	};
	brand: {
		nodes: TranslationNode[];
	};
	abTest: {
		nodes: TranslationNode[];
	};
};

type Translations = Record<string, Record<string, string>>;

class TranslationsBucket {
	static translations: Translations;

	static setTranslations(t: Translations) {
		this.translations = t;
	}
}

export default function useTranslation(ns?: string): TranslationService {
	const { params } = useProduct();
	const data: TranslationQueryResponse = useStaticQuery(graphql`
		query {
			default: allFile(
				filter: {
					relativePath: {
						regex: "/^translations/(default|patient|caregiver)$/"
					}
					gitRemote: { sourceInstanceName: { eq: "default-configuration" } }
				}
			) {
				nodes {
					name
					childPlainText {
						content
					}
				}
			}
			brand: allFile(
				filter: {
					relativePath: {
						regex: "/^translations/(default|patient|caregiver)$/"
					}
					gitRemote: { sourceInstanceName: { ne: "default-configuration" } }
				}
			) {
				nodes {
					name
					childPlainText {
						content
					}
				}
			}
			abTest: allFile(
				filter: {
					relativePath: {
						regex: "/^translations/[A-Z]_(default|patient|caregiver)$/"
					}
				}
			) {
				nodes {
					name
					childPlainText {
						content
					}
				}
			}
		}
	`);

	const option = getABFlow();

	const translations = getTranslations(data, option);

	const translate = (key: string): string => {
		const translation: string =
			translations[ns || PerformerType.PATIENT][key?.replace(/\./g, '_')] ||
			`[${key?.replace(/\./g, ' ')}]`;
		// Replace params
		//eslint-disable-next-line
		const paramsRegex = /\{([^\}]+)\}/g;
		return translation?.replace(paramsRegex, (_, p1) => {
			return params[p1.trim()] || `{${p1}}`;
		});
	};

	return { t: translate };
}
export const getOptionTranslations = (
	data: TranslationQueryResponse,
	option: TestOption
) => {
	if (option === TestOption.DEFAULT || !data.abTest) {
		return {};
	}
	const TWO_CHARS = 2;
	return (
		data.abTest.nodes
			.filter((node) => node.name.charAt(0) === option)
			.reduce((acc: Translations, node: TranslationNode) => {
				acc[node.name.slice(TWO_CHARS)] = JSON.parse(
					node.childPlainText.content
				);
				return acc;
			}, {}) ?? {}
	);
};

const createTranslations = (
	data: TranslationQueryResponse,
	testOption: TestOption
) => {
	const defaultTranslations: Translations = data.default.nodes.reduce(
		(acc: Translations, node: TranslationNode) => {
			acc[node.name] = JSON.parse(node.childPlainText.content);
			return acc;
		},
		{}
	);

	const brandTranslations: Translations = data.brand.nodes.reduce(
		(acc: Translations, node: TranslationNode) => {
			acc[node.name] = JSON.parse(node.childPlainText.content);
			return acc;
		},
		{}
	);
	const optionTranslations: Translations = getOptionTranslations(
		data,
		testOption
	);

	const mergedTranslations: Translations = Object.keys(
		brandTranslations
	).reduce((acc: Translations, key: string) => {
		acc[key] = {
			...defaultTranslations[key],
			...brandTranslations[key],
			...optionTranslations[key],
		};
		return acc;
	}, {});

	mergedTranslations[PerformerType.PATIENT] = {
		...mergedTranslations['default'],
		...mergedTranslations[PerformerType.PATIENT],
	};
	mergedTranslations[PerformerType.CAREGIVER] = {
		...mergedTranslations['default'],
		...mergedTranslations[PerformerType.CAREGIVER],
	};
	return mergedTranslations;
};

const getTranslations = (
	data: TranslationQueryResponse,
	option: TestOption
): Translations => {
	if (!TranslationsBucket.translations) {
		const translations = createTranslations(data, option);
		TranslationsBucket.setTranslations(translations);
	}
	return TranslationsBucket.translations;
};
